import { useCallback, useMemo } from 'react';

export const SESSION_STORAGE_KEY = 'z37-discovery-session';
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export function generateSessionId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.timestamp || Date.now() - parsed.timestamp > SESSION_TTL_MS) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useDiscoverySession() {
  const initial = useMemo(() => {
    const saved = readSession();
    if (saved && saved.sessionId) {
      return {
        sessionId: saved.sessionId,
        savedMessages: saved.messages || [],
        savedWireframes: saved.wireframes || [],
        savedLead: saved.lead || null,
      };
    }
    return {
      sessionId: generateSessionId(),
      savedMessages: [],
      savedWireframes: [],
      savedLead: null,
    };
  }, []);

  const saveSession = useCallback(
    ({ sessionId, messages, wireframes, lead }) => {
      try {
        localStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({
            sessionId,
            messages: messages || [],
            wireframes: wireframes || [],
            lead: lead || null,
            timestamp: Date.now(),
          }),
        );
      } catch {
        /* localStorage can throw in private modes; ignore */
      }
    },
    [],
  );

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    sessionId: initial.sessionId,
    savedMessages: initial.savedMessages,
    savedWireframes: initial.savedWireframes,
    savedLead: initial.savedLead,
    saveSession,
    clearSession,
  };
}
